/**
 * Design tokens for the public marketing site (design_handoff_fitvent_website_v8).
 *
 * A TypeScript object rather than CSS custom properties, deliberately.
 *
 * The marketing site is one page rendered inside the same Next app as the
 * organiser dashboard, and that app already carries two CSS token sets on
 * `:root`: Forge (`--fv-*`) and the 2026 redesign (`--fvd-*`). Adding a third
 * global set for a single page would put three palettes in every stylesheet the
 * app loads, and would make it far too easy for a marketing colour to leak into
 * a product surface, or for a product token change to silently restyle the
 * homepage.
 *
 * Scoping them to an imported object keeps the site's palette where the site is
 * and nowhere else. It also means the values are type-checked at the point of
 * use rather than failing silently as an undefined `var()`.
 *
 * Every value here is quoted from the handoff. Do not invent new ones: the
 * palette is small on purpose.
 */

export const W = {
  /* ── Ground ── */
  page: '#FAFCFB',
  obsidian: '#0C1312',
  tint: '#E9F4F1',

  /* ── Text ── */
  ink: '#10201D',
  bone: '#F4FAF8',
  body: '#546C66',
  bodyAlt: '#3E5650',
  mute: '#93A8A2',
  muteL: '#5A7871',
  dim: '#8FA8A2',

  /* ── Accent ──
     `teal` on light grounds, `ice` on dark. Same relationship as the app's
     brand mark, and for the same reason: #006D6D is close to invisible on
     #0C1312. */
  teal: '#006D6D',
  tealLight: '#0E8A85',
  ice: '#2ADFDF',

  /* ── Lines ── */
  rule: '#D8E6E1',
  line: '#24312E',
  borderStrong: '#C4D8D1',
  darkLine: '#1B2522',
  darkBorder: '#3A4A47',
} as const;

/**
 * Type scale, at roughly 80% of the handoff's figures.
 *
 * The handoff was composed against a 1440px artboard and its sizes are true to
 * that, but on a real 1440px browser window, inside browser chrome, the page
 * read as oversized: the header alone was taller than most sites' and the whole
 * thing looked better at 90% browser zoom. Rather than leave every visitor to
 * zoom out, the scale is applied here once. Ratios are unchanged, so the
 * relationships the design set up all survive.
 *
 * Nothing here goes below 14px: the small mono kickers and eyebrows were
 * deliberately removed from the design, and reintroducing a 12px label would
 * undo that.
 */
export const T = {
  heroH1: { fontSize: 74, fontWeight: 700, letterSpacing: '-0.048em', lineHeight: 0.98 },
  heroSub: { fontSize: 23, fontWeight: 400, letterSpacing: '-0.01em', lineHeight: 1.5 },
  h2: { fontSize: 39, fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1.08 },
  cardTitle: { fontSize: 23, fontWeight: 700, letterSpacing: '-0.028em', lineHeight: 1.18 },
  overlayTitle: { fontSize: 33, fontWeight: 700, letterSpacing: '-0.032em', lineHeight: 1.12 },
  body: { fontSize: 15.5, fontWeight: 400, lineHeight: 1.6 },
  small: { fontSize: 13.5, fontWeight: 400 },
  nav: { fontSize: 15, fontWeight: 400 },
} as const;

export const SHADOW = {
  cardHover: '0 18px 44px rgba(16,32,29,0.12)',
  panel: '0 10px 26px rgba(16,32,29,0.08)',
  productFrame: '0 24px 60px rgba(16,32,29,0.09)',
} as const;

/** The site is a single typeface with no monospace. */
export const FONT = 'var(--font-schibsted-grotesk), system-ui, sans-serif';

/** Section padding, and the fixed design width the layout is composed against. */
export const LAYOUT = {
  maxWidth: 1300,
  sectionPadding: '76px 58px',
  headerPadding: '18px 58px',
} as const;
